import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import { Box } from "@reverb-ui/react";

import "leaflet/dist/leaflet.css";
import styled from "styled-components";

const customGeojson = require("./custom-geojson.json");

const MapBox = styled(Box)`
  .leaflet-container {
    width: 100%;
    height: 100%;
  }

  .leaflet-popup-content-wrapper {
    border-radius: 4px;
  }

  .leaflet-popup-content {
    margin: 4px 8px;
    display: flex;
    justify-content: center;
  }

  .leaflet-popup-close-button {
    display: none;
  }

  .leaflet-popup-tip {
    width: 12px;
    height: 12px;
  }
`;

export type TTrafficInsightsMapMarker = {
  coordinates: [number, number];
  visitors: number;
  name: string;
};

export type TMapProps = {
  zoom?: number;
  minZoom?: number;
};

export type TTrafficInsightsMapProps = {
  geolocations: {
    country: string;
    nRespondents: number;
  }[];
  mapProps?: TMapProps;
};

const outerBounds: [[number, number], [number, number]] = [
  [-89.98155760646617, -180],
  [89.99346179538875, 180],
];

export const TrafficInsightsMap = ({
  geolocations,
  mapProps,
}: TTrafficInsightsMapProps) => {
  useEffect(() => {
    const mapContainer = document.getElementById("traffic-map-container");

    // Force map resize on every resize of the component
    if (mapContainer) {
      new ResizeObserver(() =>
        window.dispatchEvent(new Event("resize"))
      ).observe(mapContainer);
    }
  }, []);

  const largestTraffic = useMemo(() => {
    let largest = 0;
    geolocations.forEach(({ nRespondents }) => {
      largest += nRespondents;
    });

    return largest;
  }, [geolocations]);

  const data = useMemo(() => {
    const features = geolocations
      .filter(({ country }) => customGeojson[country])
      .map(({ country, nRespondents }) => {
        const countryData = customGeojson[country];
        return {
          ...countryData,
          properties: {
            ...countryData.properties,
            color: "rgba(34, 94, 216, 1)",
            respondents: nRespondents,
          },
        };
      });

    return {
      type: "FeatureCollection",
      features,
    };
  }, [customGeojson, geolocations]);

  const geoJsonKey = useMemo(() => {
    return new Date().toJSON();
  }, [data]);

  const onEachCountry = (country: any, layer: any) => {
    const normalizedValue = country.properties.respondents / largestTraffic;
    const getColor = (value: number) => {
      if (value <= 0.25) return "#ccddfa";
      if (value <= 0.5) return "#9bbaf6";
      if (value <= 0.75) return "#6898f2";

      return "#235ed8";
    };

    layer.options.fillColor = getColor(normalizedValue);
    layer.bindPopup(
      `${country.properties.name} - ${country.properties.respondents}`
    );
    layer.on("mouseover", () => {
      layer.setStyle({ weight: 3 });
    });

    layer.on("mouseout", () => {
      layer.setStyle({ weight: 1 });
    });
  };

  return (
    <MapBox h="full" w="full" id="traffic-map-container">
      <MapContainer
        center={[10.4434223, -34.3117367]}
        zoom={mapProps?.zoom ?? 2}
        scrollWheelZoom
        bounds={outerBounds}
        maxBounds={outerBounds}
        maxBoundsViscosity={1}
        attributionControl={false}
        minZoom={mapProps?.minZoom ?? 1.5}
      >
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png" />
        <GeoJSON
          key={geoJsonKey}
          data={data as any}
          onEachFeature={onEachCountry}
          style={{
            fillOpacity: 10,
            weight: 1,
            color: "#2D3648",
          }}
        />
      </MapContainer>
    </MapBox>
  );
};
